import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

/**
 * Hook for fetching and managing cities data
 * @param {Object} options - Configuration options
 * @param {boolean} [options.autoFetch=true] - Whether to fetch data automatically on mount
 * @returns {Object} Cities data and functions
 */
const useCities = ({ autoFetch = true } = {}) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch cities data
  const fetchCities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/cities/');
      setCities(response.data);
    } catch (err) {
      console.error('Error fetching cities:', err);
      setError(err.message || 'Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  }, []);

  // Get a single city by ID
  const getCity = useCallback(async (cityId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/cities/${cityId}`);
      return response.data;
    } catch (err) {
      console.error(`Error fetching city ${cityId}:`, err);
      setError(err.message || 'Failed to fetch city');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new city
  const createCity = useCallback(async (cityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/cities/', cityData);
      setCities((prevCities) => [...prevCities, response.data]);
      return response.data;
    } catch (err) {
      console.error('Error creating city:', err);
      setError(err.message || 'Failed to create city');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing city
  const updateCity = useCallback(async (cityId, cityData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put(`/cities/${cityId}`, cityData);
      setCities((prevCities) =>
        prevCities.map((city) =>
          city.id === cityId ? response.data : city
        )
      );
      return response.data;
    } catch (err) {
      console.error(`Error updating city ${cityId}:`, err);
      setError(err.message || 'Failed to update city');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a city
  const deleteCity = useCallback(async (cityId) => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/cities/${cityId}`);
      setCities((prevCities) =>
        prevCities.filter((city) => city.id !== cityId)
      );
      return true;
    } catch (err) {
      console.error(`Error deleting city ${cityId}:`, err);
      setError(err.message || 'Failed to delete city');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on mount if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchCities();
    }
  }, [autoFetch, fetchCities]);

  return {
    cities,
    loading,
    error,
    fetchCities,
    getCity,
    createCity,
    updateCity,
    deleteCity,
  };
};

export default useCities; 